import {Grid} from '@material-ui/core';
import {FC, useState} from 'react';
import RootStore from '../../Core/mobx/RootStore';
import EmailEditorDialog from '../ContactForm/EmailEditor/EmailEditorDialog';
import {TemplatePurposes, TemplateTypes} from '../ContactForm/EmailEditor/EmailEditor';
import {ProductTypes} from '../types';
import {observer} from 'mobx-react-lite';
import {useTemplateData} from '../../Core/hooks/useTemplateData';
import {QuoteCombinationInfo} from './QuoteCombinationInfo';
import {QuotesTableData} from './QuotesTableData';
import {onEditorSend} from '../ContactForm/EmailEditor/EmailEditorSelection';
import {ParameterStore} from '../../Core/mobx/ParameterStore/ParameterStore';
import {QuoteHistory} from '../../Core/mobx/ParameterStore/types';
import {useAgentAdminData} from '../../Core/hooks/useAgentAdminData';

interface QuotesTableProps {
    data: QuoteHistory | null;
    formType?: 'single-quote' | 'combination-quote' | undefined;
    parameterStore: ParameterStore;
    handleModal?: boolean;
}

export const QuotesTable: FC<QuotesTableProps> = observer(({data, formType, parameterStore, handleModal = false}) => {
    const [openEmailEditor, setOpenEmailEditor] = useState(false);

    const [emailTemplateType, setEmailTemplateType] = useState<TemplateTypes>(TemplateTypes.general);
    const [emailTemplatePurpose, setEmailTemplatePurpose] = useState<TemplatePurposes>(TemplatePurposes.quoted);

    const age = parameterStore.approval?.age.value;
    const email = RootStore.agentPortalStore.contactsFormStore.contact ?? parameterStore?.contact;
    const isCombination = data?.combinedQuote;
    const templateData = useTemplateData(data?.singleQuote || null, data?.combinedQuote || null);
    const {agentData} = useAgentAdminData();

    const onEditorClose = () => {
        setOpenEmailEditor(false);
    };

    const showEmailEditor = (purpose: TemplatePurposes) => {
        if (!email) {
            RootStore.uiStore.showSnackbar('Email address is empty', 'error');
            return;
        }

        const ptype = data?.singleQuote?.productType;
        let templateType: TemplateTypes = TemplateTypes.general;
        switch (ptype) {
            case ProductTypes.term:
                templateType = TemplateTypes.quoteTermNonblend;
                break;
            case ProductTypes.lswiul:
            case ProductTypes.iul:
                templateType = TemplateTypes.quoteIulNonblend;
                break;
            case ProductTypes.prosper:
                templateType = TemplateTypes.quoteProsperIULNonblend;
                break;
            case ProductTypes.gul:
                templateType = TemplateTypes.quoteGulNonblend;
                break;
            case ProductTypes.guaranteed:
                templateType = TemplateTypes.quoteGuaranteedNonblend;
                break;
            case ProductTypes.vul:
                templateType = TemplateTypes.quoteVULNonblend;
                break;
            default:
                break;
        }

        if (isCombination) {
            const singleQuoteType = data?.singleQuote?.productType;
            const combinedQuoteType = data?.combinedQuote?.productType;

            const checkCombination = (first: string, second: string) => {
                return (
                    (singleQuoteType === first && combinedQuoteType === second) ||
                    (singleQuoteType === second && combinedQuoteType === first)
                );
            };

            if (checkCombination('GUL', 'Term')) {
                templateType = TemplateTypes.quoteGULTermBlend;
            } else if (checkCombination('IUL', 'Term') || checkCombination('LSWIUL', 'Term')) {
                templateType = TemplateTypes.quoteIULTermBlend;
            } else if (checkCombination('VUL', 'Term')) {
                templateType = TemplateTypes.quoteVULTermBlend;
            }
        }

        setEmailTemplatePurpose(purpose);
        setEmailTemplateType(templateType);
        setOpenEmailEditor(true);
    };

    return (
        <>
            {data?.combinedQuote && !formType ? (
                <>
                    <QuoteCombinationInfo singleQuote={data?.singleQuote} combinedQuote={data?.combinedQuote} />
                    <Grid spacing={2} container style={{marginTop: 30}}>
                        <Grid item xs={6}>
                            <QuotesTableData
                                out={data?.singleQuote?.out}
                                age={age}
                                email={email}
                                data={data}
                                parameterStore={parameterStore}
                                formType={formType}
                                showEmailEditor={showEmailEditor}
                                handleModal={handleModal}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <QuotesTableData
                                out={data?.combinedQuote?.out}
                                age={age}
                                data={data}
                                email={email}
                                parameterStore={parameterStore}
                                formType={formType}
                                showEmailEditor={showEmailEditor}
                                handleModal={handleModal}
                            />
                        </Grid>
                    </Grid>
                </>
            ) : (
                <QuotesTableData
                    out={formType === 'single-quote' || !formType ? data?.singleQuote?.out : data?.combinedQuote?.out}
                    age={age}
                    email={email}
                    data={data}
                    parameterStore={parameterStore}
                    formType={formType}
                    showEmailEditor={showEmailEditor}
                    handleModal={handleModal}
                />
            )}

            <EmailEditorDialog
                open={openEmailEditor}
                onClose={onEditorClose}
                onSend={(
                    emails: string[],
                    html: string,
                    productType: string,
                    templatePurpose: TemplatePurposes,
                    bccEmails?: string[],
                    subject?: string,
                    illustration?: string,
                ) => {
                    onEditorSend(emails, html, productType, templatePurpose, bccEmails, subject, illustration).then(
                        () => {
                            onEditorClose();
                        },
                    );
                }}
                currentEmails={[email]}
                templatePurpose={emailTemplatePurpose}
                templateType={emailTemplateType}
                templateData={[{...templateData, managerName: agentData?.managerName}]}
            ></EmailEditorDialog>
        </>
    );
});
